import { useState, useEffect, useRef } from 'react';

interface CreateToolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description?: string; promptDefinition: string }) => Promise<void>;
  isSubmitting?: boolean;
  error?: string;
}

/**
 * Modal component for creating a new prompt-based tool.
 *
 * Provides a form for administrators to define a new tool with:
 * - Name (required)
 * - Description (optional)
 * - AI Prompt Definition (required)
 */
export function CreateToolModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  error,
}: CreateToolModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [promptDefinition, setPromptDefinition] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setPromptDefinition('');
      setValidationError(null);
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validate required fields
    if (!name.trim()) {
      setValidationError('Tool name is required');
      return;
    }

    if (!promptDefinition.trim()) {
      setValidationError('AI prompt definition is required');
      return;
    }

    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        promptDefinition: promptDefinition.trim(),
      });
    } catch (err) {
      // Error handling is done by parent component
    }
  };

  const isValid = name.trim() && promptDefinition.trim();

  return (
    <div style={styles.overlay}>
      <div ref={modalRef} style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}>✨</span>
            <div>
              <h2 style={styles.title}>Create Tool</h2>
              <p style={styles.subtitle}>Define a new prompt-based tool</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.content}>
            {/* Error Display */}
            {(validationError || error) && (
              <div style={styles.errorMessage}>
                {validationError || error}
              </div>
            )}

            {/* Tool Type Info */}
            <div style={styles.infoBox}>
              <div style={styles.infoIcon}>💡</div>
              <div style={styles.infoContent}>
                <p style={styles.infoTitle}>Prompt-based Tool</p>
                <p style={styles.infoText}>
                  This tool will be powered by AI using the prompt definition you provide.
                  It will be available in the Bot Builder for all bots.
                </p>
              </div>
            </div>

            {/* Name Field */}
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="tool-name">
                Tool Name <span style={styles.required}>*</span>
              </label>
              <input
                id="tool-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Mood Analyzer, Daily Tip Generator"
                style={styles.input}
                maxLength={100}
                disabled={isSubmitting}
                autoFocus
              />
              <span style={styles.helpText}>
                A unique, descriptive name for the tool (max 100 characters)
              </span>
            </div>

            {/* Description Field */}
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="tool-description">
                Description
              </label>
              <textarea
                id="tool-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this tool does and when it should be used..."
                style={styles.textarea}
                rows={3}
                maxLength={500}
                disabled={isSubmitting}
              />
              <span style={styles.helpText}>
                Optional description to help understand the tool's purpose (max 500 characters)
              </span>
            </div>

            {/* Prompt Definition Field */}
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="tool-prompt">
                AI Prompt Definition <span style={styles.required}>*</span>
              </label>
              <textarea
                id="tool-prompt"
                value={promptDefinition}
                onChange={(e) => setPromptDefinition(e.target.value)}
                placeholder={`Example:
You are a mood analyzer tool. When activated, analyze the user's recent messages to determine their emotional state.

Consider these factors:
- Word choice and tone
- Expressed concerns or joys
- Energy level indicators

Respond with:
1. Primary mood detected
2. Confidence level (1-10)
3. Brief explanation
4. Suggested supportive response`}
                style={{
                  ...styles.textarea,
                  ...styles.promptTextarea,
                }}
                rows={10}
                disabled={isSubmitting}
              />
              <span style={styles.helpText}>
                The AI instructions that define how this tool behaves when invoked
              </span>
            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button
              type="button"
              onClick={onClose}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                ...((!isValid || isSubmitting) ? styles.submitButtonDisabled : {}),
              }}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span style={styles.spinner} />
                  Creating...
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Create Tool
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    fontSize: '24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    fontFamily: '"Sentient", Georgia, serif',
  },
  subtitle: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.06)',
    cursor: 'pointer',
    color: '#706A62',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
  },
  content: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  },
  errorMessage: {
    padding: '12px 16px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  infoBox: {
    display: 'flex',
    gap: '12px',
    padding: '14px 16px',
    background: 'linear-gradient(135deg, rgba(90, 106, 138, 0.08) 0%, rgba(90, 138, 94, 0.08) 100%)',
    borderRadius: '10px',
    border: '1px solid rgba(90, 106, 138, 0.15)',
    marginBottom: '20px',
  },
  infoIcon: {
    fontSize: '18px',
    flexShrink: 0,
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#5A6A8A',
    margin: '0 0 4px 0',
  },
  infoText: {
    fontSize: '11px',
    color: '#706A62',
    lineHeight: '1.5',
    margin: 0,
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '8px',
  },
  required: {
    color: '#8B5A50',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
  },
  promptTextarea: {
    fontFamily: 'inherit',
    lineHeight: '1.6',
  },
  helpText: {
    display: 'block',
    fontSize: '11px',
    color: '#9B9285',
    marginTop: '6px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  cancelButton: {
    padding: '12px 20px',
    backgroundColor: 'white',
    color: '#5C564E',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  submitButton: {
    padding: '12px 20px',
    backgroundColor: '#3D3A36',
    color: '#FAF9F7',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.15s ease',
  },
  submitButtonDisabled: {
    backgroundColor: '#9B9285',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '14px',
    height: '14px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

export default CreateToolModal;
